'use client';

import { useEditor, EditorContent } from '@tiptap/react';
import { useEffect, useRef, useCallback } from 'react';
import { getTiptapExtensions } from './utils/tiptapExtensions';
import styles from './TiptapEditor.module.css';

function deepEqualJson(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

export default function TiptapEditor({
  content,
  onChange,
  isEditable = true,
  editorRef = null,
  headings = [],
  onImageClick = null,
}) {
  const wrapperRef = useRef(null);

  const editor = useEditor({
    extensions: getTiptapExtensions(),
    content,

    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      onChange?.(json);
    },
    editable: isEditable,
    autofocus: isEditable ? 'end' : false,
  });

  useEffect(() => {
    if (!editor) return;

    editor.setEditable(isEditable);

    if (isEditable) {
      setTimeout(() => {
        try {
          editor.view.focus();
        } catch (e) {}
      }, 50);
    }
  }, [editor, isEditable]);

  useEffect(() => {
    if (editorRef) {
      editorRef.current = editor;
    }
  }, [editor, editorRef]);

  const lastIncoming = useRef(null);

  // Handle image clicks in read-only mode only
  useEffect(() => {
    if (!editor) return;
    if (!onImageClick) return;

    const handleImageClick = (event) => {
      // Only handle clicks in read-only mode (not editing)
      if (isEditable) return;

      const target = event.target;
      // Check if clicked on image or its wrapper
      const img = target.tagName === 'IMG' ? target : target.querySelector('img');
      if (img && img.src) {
        event.preventDefault();
        event.stopPropagation();

        // Get the actual src attribute value, not the computed src
        const actualSrc = img.getAttribute('src') || img.src;
        onImageClick(actualSrc);
      }
    };

    // Add click listener to editor element with capture phase
    const editorElement = editor.view.dom;
    editorElement.addEventListener('click', handleImageClick, true);

    return () => {
      editorElement.removeEventListener('click', handleImageClick, true);
    };
  }, [editor, onImageClick, isEditable]);

  useEffect(() => {
    if (!editor) return;
    if (!content || typeof content !== 'object') return;

    const normalizedContent = (content?.type === 'doc' && Array.isArray(content?.content)) ? content : { type: 'doc', content: [] };

    if (lastIncoming.current && deepEqualJson(lastIncoming.current, normalizedContent)) {
      return;
    }

    const current = editor.getJSON();
    if (!deepEqualJson(current, normalizedContent)) {
      try {
        editor.commands.setContent(normalizedContent, false);
      } catch (err) {
        editor.commands.setContent(normalizedContent);
      }
    }

    lastIncoming.current = normalizedContent;
  }, [editor, content]);

  useEffect(() => {
    if (!editor) return;
    if (!Array.isArray(headings) || headings.length === 0) return;

    setTimeout(() => {
      const root = editor.view.dom;
      if (!root) return;

      headings.forEach((h) => {
        const level = h.level || 1;
        const tag = `H${level}`;
        const elements = root.querySelectorAll(tag);
        for (const el of elements) {
          const text = (el.textContent || '').trim();

          if (text === (h.text || '').trim()) {
            if (el.id !== h.id) el.id = h.id;
            break;
          }
        }
      });
    }, 0);
  }, [editor, headings, content]);

  if (!editor) {
    return <div className={styles.editorPlaceholder}>Loading editor...</div>;
  }

  return (
    <div
      ref={wrapperRef}
      className={`${styles.editor} ${isEditable ? styles.editable : styles.readOnly}`}
    >
      <EditorContent editor={editor} className={styles.editorContent} />
    </div>
  );
}
